// src/components/AuthGate.tsx
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthProvider';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useFirestore } from '@/hooks/useFirestore';
import { setDoc } from 'firebase/firestore';
import { firstQuestionSubject, FIRST_QUESTION_YEAR } from '@/utils/funnel';

interface Props {
  children: React.ReactNode;
}

// 認証状態とチュートリアル導線を制御するゲートコンポーネント
// ログインなしでもすべてのコンテンツにアクセス可能
const AuthGate: React.FC<Props> = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const firestore = useFirestore();

  // 新規登録後のチュートリアル誘導
  //
  // 【2026-09-23 修正】以前は root の '/tutorial' へ遷移していたが、実際のルートは
  // '/:appId/tutorial' で、root の '/tutorial' は Router で '/' へリダイレクトされる。
  // その結果 新規登録直後のユーザーが '/tutorial' ⇄ '/' の無限ループに入り、
  // アプリが一切使えない状態だった（2025-11-23 のリダイレクト追加以降）。
  // → アプリ配下の正しいパスへ遷移し、1マウントにつき1回だけ試みる。
  const redirectedRef = React.useRef(false);
  useEffect(() => {
    if (loading || !user) return;
    if (redirectedRef.current) return;

    // ⚠️ **ここは競争になる。**（2026-10-02 に実際に壊れていた）
    // 初回登録の印 `needsTutorial='1'` を書くのは AuthProvider で、
    // しかも **`setUser()` の後、`getDoc` → `setDoc` を待ってから**書いている。
    // つまりこの効果が走る時点では、**まだ印が無いことがある**。
    // 以前は「印が無ければ return」だけで、依存配列が変わらない限り二度と見に来なかったため、
    // **印が後から立っても誰も気づかず、最初の1問へ飛ばないまま**になっていた。
    // 導線ガードで「0クリックなのに着かない」として検出。**短い間だけ見張り続ける**ことで直す。
    let cancelled = false;
    let tries = 0;

    /** 印が立っていれば遷移して true。まだなら false（呼び直す） */
    const attempt = (): boolean => {
      if (cancelled || redirectedRef.current) return true;
      let needsTutorial = false;
      try {
        needsTutorial = localStorage.getItem('needsTutorial') === '1';
      } catch (_) {
        return true; // localStorage が使えない環境では誘導しない（見張りも止める）
      }
      if (!needsTutorial) return false;

      // '/chusho-app/...' のようにアプリIDが付いたパス構成。
      // 【2026-09-26 修正】先頭セグメントを無条件に appId とみなしていたため、
      // appId 無しの絶対パス（/account など）から来ると '/account/tutorial' という
      // 存在しないパスへ飛ばしていた（導線ガードが検出）。**既知のアプリIDのみ採用する。**
      const KNOWN_APP_IDS = ['chusho-app', 'ghouseishosi'];
      const segments = location.pathname.split('/').filter(Boolean);
      const appId = KNOWN_APP_IDS.includes(segments[0]) ? segments[0] : 'chusho-app';

      redirectedRef.current = true;

      // 【2026-09-29 変更】チュートリアル画面を挟まず、**最初の1問へ直行**する。
      // 実測（scripts/e2e/first-question-steps.mjs）: 登録直後 → 最初の1問 は
      // 「最初の1問を解く」を押す **1クリック**だった。その1回を無くす。
      // 画面は残してあるので `/:appId/tutorial` を直接開けば今までどおり読める。
      // 画面を飛ばすと「解いた記録が残る」ことを伝える場所が無くなるので、Snackbar で1行だけ伝える。
      try {
        localStorage.setItem('needsTutorial', '0');
      } catch (_) {
        // localStorage が使えない環境では無視（遷移は続ける）
      }
      // 失敗しても導線は止めない（チュートリアル画面と同じ扱い）
      setDoc(firestore.getUserDoc(user.uid), { onboardingCompleted: true }, { merge: true }).catch((e) =>
        console.warn('オンボーディング完了フラグの保存に失敗しました:', e)
      );
      const subject = firstQuestionSubject();
      navigate(`/${appId}/subjects/${subject}/${FIRST_QUESTION_YEAR}`, { replace: true });
      enqueueSnackbar('まず1問どうぞ。解いた記録は自動で残るので、次は続きから始められます。', {
        variant: 'info',
        autoHideDuration: 6000,
      });
      return true;
    };

    if (attempt()) return;
    // 既存ユーザーなら印は立たない。**約6秒で見張りをやめる**（無駄に回し続けない）。
    const id = setInterval(() => {
      tries += 1;
      if (attempt() || tries > 20) clearInterval(id);
    }, 300);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [loading, user, navigate, location.pathname, enqueueSnackbar, firestore]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
      </Box>
    );
  }

  // ログインの有無に関わらず、すべてのコンテンツを表示
  return <>{children}</>;
};

export default AuthGate;

